import { RecommendedLearningKindType } from "@devforce/trailhead-client-gql";
import { logger } from "@devforce/trailhead-client-logger";

export interface DismissModalState {
  id: string;
  type: string;
}

/**
 * Dismisses a recommendation via GraphQL mutation
 * @param modalData - The recommendation to dismiss (id and type)
 * @returns Promise that resolves on success, rejects on failure
 */
export async function dismissRecommendation(
  modalData: DismissModalState
): Promise<void> {
  const { id, type } = modalData;
  if (!id || !type) return;

  const { getClient, gql } = await import("@devforce/trailhead-client-apollo");

  try {
    await getClient().mutate({
      mutation: gql`
        mutation DismissRecommendedLearning(
          $input: DismissRecommendedLearningInput!
        ) {
          dismissRecommendedLearning(input: $input) {
            id
            type
          }
        }
      `,
      variables: {
        input: {
          id,
          type: type as RecommendedLearningKindType,
        },
      },
    });
  } catch (error) {
    logger.error(
      `Failed to dismiss recommendation: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    throw error;
  }
}

/**
 * Extracts dismiss action data from a content collection action event
 * @param event - Custom event from content collection
 * @returns DismissModalState if action is "not_interested", null otherwise
 */
interface DismissActionEventDetail {
  action: {
    name: string;
  };
  config?: {
    recommendationId?: string;
    recommendationType?: string;
  };
}

export function extractDismissAction(
  event: CustomEvent<DismissActionEventDetail>
): DismissModalState | null {
  const { action, config } = event.detail;

  if (
    action.name === "not_interested" &&
    config?.recommendationId &&
    config?.recommendationType
  ) {
    return {
      id: config.recommendationId,
      type: config.recommendationType,
    };
  }

  return null;
}
